import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, message } from 'antd';
import moment from 'moment';
import { getAllAccountList } from './SavingService';
import { errHandler } from '../../helper/helper';
import { exportAccountListExcelReport } from "../../Utils/AccountListExcelExport";

const { Option } = Select;

const AccountList = ({
                         loginPlaceId,
                         districtData,
                         districtName,
                         divisionalId,
                         branchId,
                         products = [],
                     }) => {
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(0);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);

    const statusOptions = [
        { value: 0, label: 'All Accounts' },
        { value: 1, label: 'Active Accounts' },
        { value: 2, label: 'Dormant Accounts' },
        { value: 3, label: 'Inactive Accounts' },
        { value: 5, label: 'Closed Accounts' }
    ];

    // Initialize selections based on loginPlaceId
    useEffect(() => {
        if (loginPlaceId === "1" && branchId) setSelectedBranches([parseInt(branchId)]);
        else if (loginPlaceId === "2" && divisionalId) setSelectedDivisionalAreas([parseInt(divisionalId)]);
        else if (loginPlaceId === "4" && districtName) {
            const userDistrict = districtData.find(d => d.districtName === districtName);
            if (userDistrict) setSelectedDistricts([userDistrict.districtId]);
        }
    }, [loginPlaceId, branchId, divisionalId, districtName, districtData]);

    // Update available divisional areas when districts change
    useEffect(() => {
        let filteredDivAreas = [];
        if (loginPlaceId === "2" && divisionalId) {
            filteredDivAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === parseInt(divisionalId));
        } else {
            if (selectedDistricts.length > 0) {
                filteredDivAreas = districtData
                    .filter(d => selectedDistricts.includes(d.districtId))
                    .flatMap(d => d.divisionalAreaDtoList || []);
            } else {
                filteredDivAreas = districtData.flatMap(d => d.divisionalAreaDtoList || []);
            }
        }
        setAvailableDivisionalAreas(filteredDivAreas);
    }, [selectedDistricts, districtData, loginPlaceId, divisionalId]);

    // Update available branches when divisional areas change
    useEffect(() => {
        let filteredBranches = [];
        if (loginPlaceId === "1") {
            const userBranch = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || [])
                .find(b => b.id === parseInt(branchId));
            if (userBranch) filteredBranches = [userBranch];
        } else if (loginPlaceId === "2") {
            if (selectedDivisionalAreas.length > 0) {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => selectedDivisionalAreas.includes(div.divAId))
                    .flatMap(div => div.branchDtoList || []);
            } else {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => div.divAId === parseInt(divisionalId))
                    .flatMap(div => div.branchDtoList || []);
            }
        } else {
            if (selectedDivisionalAreas.length > 0) {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => selectedDivisionalAreas.includes(div.divAId))
                    .flatMap(div => div.branchDtoList || []);
            } else if (selectedDistricts.length > 0) {
                filteredBranches = districtData
                    .filter(d => selectedDistricts.includes(d.districtId))
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .flatMap(div => div.branchDtoList || []);
            } else {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .flatMap(div => div.branchDtoList || []);
            }
        }
        setAvailableBranches(filteredBranches);
    }, [selectedDivisionalAreas, selectedDistricts, districtData, loginPlaceId, divisionalId, branchId]);


    const getStatusList = (statusValue) => {
        switch (statusValue) {
            case 0:
                return [0, 1, 2, 3, 4, 5];
            case 1:
                return [0, 2, 4];
            case 2:
                return [3];
            case 3:
                return [1];
            case 5:
                return [5];
            default:
                return [0, 1, 2, 3, 4, 5];
        }
    };

    const getStatusLabel = (statusValue) => {
        const statusOption = statusOptions.find(opt => opt.value === statusValue);
        return statusOption ? statusOption.label : 'Unknown Status';
    };

    const handleDistrictChange = (districts) => {
        if (districts.includes('ALL')) {
            if (loginPlaceId === "4") {
                const userDistrict = districtData.find(d => d.districtName === districtName);
                if (userDistrict) setSelectedDistricts([userDistrict.districtId]);
            } else {
                setSelectedDistricts(districtData.map(d => d.districtId));
            }
        } else {
            setSelectedDistricts(districts);
        }
        setSelectedDivisionalAreas([]);
        setSelectedBranches([]);
    };

    const handleDivAreaChange = (divAreas) => {
        if (divAreas.includes('ALL')) setSelectedDivisionalAreas(availableDivisionalAreas.map(d => d.divAId));
        else setSelectedDivisionalAreas(divAreas);
        setSelectedBranches([]);
    };

    const handleBranchChange = (branches) => {
        if (branches.includes('ALL')) setSelectedBranches(availableBranches.map(b => b.id));
        else setSelectedBranches(branches);
    };

    const handleStatusChange = (value) => {
        setSelectedStatus(value);
    };

    const generateAccountListReport = async () => {
        let branchIdList = [];
        if (loginPlaceId === "1") branchIdList = [parseInt(branchId)];
        else if (selectedBranches.length > 0) branchIdList = selectedBranches.map(id => parseInt(id));
        else branchIdList = availableBranches.map(b => b.id);

        if (branchIdList.length === 0) {
            message.warning('No branches available for selection');
            return;
        }

        setLoading(true);

        try {
            // Create request data with the list of branches
            const requestData = {
                branchIdList: branchIdList,
                statusList: getStatusList(selectedStatus),
                productIdList: selectedProducts.length > 0 ? selectedProducts : null,
                sortBy: "accNumber"
            };

            const response = await getAllAccountList(requestData);
            const responseData = response.data || response;
            
            let accountData = [];
            if (responseData) {
                if (Array.isArray(responseData)) {
                    accountData = responseData;
                } else if (responseData.content !== undefined) {
                    accountData = responseData.content;
                }
            }

            const branchNameMap = new Map(availableBranches.map(b => [b.id, b.branchName]));
            
            const seenAccountNumbers = new Set();
            const allAccountData = [];

            accountData.forEach(account => {
                const accNo = account.accNumber || 'N/A';
                if (!seenAccountNumbers.has(accNo)) {
                    seenAccountNumbers.add(accNo);
                    const currentBranchId = account.branchId ? parseInt(account.branchId) : null;
                    allAccountData.push({
                        accountNumber: accNo,
                        productName: account.productName || 'N/A',
                        customerNumber: account.cusNo || 'N/A',
                        nicNumber: account.nicNo || 'N/A',
                        fullName: account.fullName || 'N/A',
                        branchName: branchNameMap.get(currentBranchId) || account.branchName || 'N/A',
                        districtName: account.districtName || 'N/A',
                        accountBalance: account.balanceAmount || 0
                    });
                }
            });

            if (allAccountData.length === 0) {
                message.info('No accounts found for the selected criteria');
                setLoading(false);
                return;
            }

            const branchNames = availableBranches
                .filter(b => branchIdList.includes(b.id))
                .map(b => b.branchName)
                .join(', ');

            const districtNames = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .map(d => d.districtName)
                .join(', ');

            const productNames = selectedProducts.length > 0
                ? products.filter(p => selectedProducts.includes(p.productId)).map(p => p.productName).join(', ')
                : 'All Products';

            // ONE Excel file export
            await exportAccountListExcelReport({
                data: allAccountData,
                fileName: `Account_List_${getStatusLabel(selectedStatus).replace(/\s+/g, '_')}_${new Date().getTime()}.xlsx`,
                meta: {
                    reportDate: new Date().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    districtName: districtNames || districtName || "All Districts",
                    branchName: branchNames || "All Branches",
                    status: getStatusLabel(selectedStatus),
                    products: productNames,
                    totalAccounts: allAccountData.length
                },
            });

            message.success(`Account List Report generated successfully! Found ${allAccountData.length} accounts.`);
        } catch (err) {
            errHandler(err);
            message.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Card hoverable className="inner-card">
            <Form layout="vertical">
                <Row gutter={[16, 16]}>

                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selectedDistricts}
                                        onChange={handleDistrictChange}
                                        disabled={loginPlaceId === "4"}
                                    >
                                        {loginPlaceId === "4" ? (
                                            districtData.filter(d => d.districtName === districtName).map(d => (
                                                <Option key={d.districtId} value={d.districtId}>🏙️ {d.districtName}</Option>
                                            ))
                                        ) : (
                                            <>
                                                <Option value="ALL">🔘 Select All Districts</Option>
                                                {districtData.map(d => (
                                                    <Option key={d.districtId} value={d.districtId}>🏙️ {d.districtName}</Option>
                                                ))}
                                            </>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selectedDivisionalAreas}
                                        onChange={handleDivAreaChange}
                                        disabled={availableDivisionalAreas.length === 0 || loginPlaceId === "2"}
                                    >
                                        <Option value="ALL">🔘 Select All Divisional Areas</Option>
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>🏙️ {d.divAIdName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Select Branches">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selectedBranches}
                                    onChange={handleBranchChange}
                                    disabled={availableBranches.length === 0 || loginPlaceId === "1"}
                                >
                                    <Option value="ALL">🔘 Select All Branches</Option>
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>🏢 {branch.branchName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Account Status">
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    style={{ width: '100%' }}
                                >
                                    {statusOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedProducts(
                                                products.map(p => p.productId)
                                            );
                                        } else {
                                            setSelectedProducts(values);
                                        }
                                    }}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Products
                                    </Option>
                                    {products && products.map(product => (
                                        <Option key={product.productId} value={product.productId}>
                                            {product.productName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateAccountListReport}
                                    disabled={loading || availableBranches.length === 0}
                                    className="generate-btn"
                                    size="large"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>
            </Card>
    );
};

export default AccountList;